/**
 * State Style04 - Presence hero (detected/occupancy)
 */
import type { StateControlProps, StateLifecycle } from './stateStyleTypes'
import {
  getStateLifecycleColor,
  inferStateLifecycle,
} from './traitStyleConfig'
import './StateStyle04.css'

const LIFECYCLE_LABEL: Record<StateLifecycle, string> = {
  live: 'LIVE',
  warn: 'WARN',
  error: 'ERROR',
  stale: 'STALE',
  offline: 'OFFLINE',
}

function toDetected(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return Number.isFinite(value) ? value > 0 : false
  const token = String(value ?? '')
    .trim()
    .toLowerCase()
  if (!token || token === '—') return false
  if (['true', '1', 'on', 'open', 'detected', 'occupied', 'present', 'active', 'yes'].includes(token)) return true
  if (['false', '0', 'off', 'closed', 'clear', 'absent', 'inactive', 'no'].includes(token)) return false
  return false
}

export function StateStyle04({
  stateKey,
  value,
  label,
  lifecycle,
  updatedAtLabel,
  thresholdEnabled,
  disabled = false,
}: StateControlProps) {
  const displayLabel = label || 'Presence'
  const resolvedLifecycle =
    lifecycle ??
    (disabled
      ? 'offline'
      : inferStateLifecycle(stateKey, value, {
          thresholdEnabled,
        }))
  const lifecycleColor = getStateLifecycleColor(resolvedLifecycle)
  const updatedLabel = String(updatedAtLabel || '2m 前更新')
  const badgeText = LIFECYCLE_LABEL[resolvedLifecycle]
  const isDetected = toDetected(value)
  const statusText = isDetected ? 'Present' : 'Clear'
  const statusBadge = isDetected ? 'Occupied' : 'No Motion'
  const heroIcon = isDetected ? 'person_check' : 'person_off'

  return (
    <div
      className="trait-state-style04"
      data-css-slot="stateContainer"
      data-css-slot-label="State Container"
      data-css-state={resolvedLifecycle}
    >
      <div className="trait-state-style04__title" data-css-slot="stateLabel" data-css-slot-label="State Label">
        {displayLabel}
      </div>
      <div className={`trait-state-style04__hero ${isDetected ? 'is-detected' : 'is-clear'}`}>
        <div className="trait-state-style04__ring is-inner" />
        <div className="trait-state-style04__ring is-outer" />
        <div className="trait-state-style04__center">
          <span
            className="material-symbols-outlined trait-state-style04__icon"
            data-css-slot="stateIcon"
            data-css-slot-label="State Icon"
            data-css-state={resolvedLifecycle}
          >
            {heroIcon}
          </span>
          <div className="trait-state-style04__status" data-css-slot="stateValue" data-css-slot-label="State Value">
            {statusText}
          </div>
          <span className="trait-state-style04__presence-badge">{statusBadge}</span>
        </div>
      </div>
      <div className="trait-state-style04__meta">
        <span
          className="trait-state-style04__badge"
          data-css-slot="stateBadge"
          data-css-slot-label="State Lifecycle Badge"
          data-css-state={resolvedLifecycle}
          style={{ '--state-lifecycle-color': lifecycleColor } as React.CSSProperties}
        >
          {badgeText}
        </span>
        <div
          className="trait-state-style04__updated"
          data-css-slot="stateUpdatedAt"
          data-css-slot-label="State Updated Time"
          data-css-state={resolvedLifecycle}
        >
          <span className="material-symbols-outlined">schedule</span>
          {updatedLabel}
        </div>
      </div>
    </div>
  )
}
